import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  Paper,
  TextField,
  Autocomplete,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import DescriptionIcon from "@mui/icons-material/Description";
import CloseIcon from "@mui/icons-material/Close";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { debounce } from "lodash";
import { buscarImportacoes, buscarLogsImportacao, buscarEstatisticas, buscarTipos } from "../../services/api-importadores.service";
import GenericDatePicker from "../../components/GenericDatePicker";
import BlueButton from "../../components/BlueButton";
import WhiteButton from "../../components/WhiteButton";
import Loading from "../../components/Loading";
import styles from "./styles.module.css";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AssignmentIcon from "@mui/icons-material/Assignment";

const Importadores = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingFiltro, setLoadingFiltro] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState("lista");

  // Filtros
  const [filtrosAvancados, setFiltrosAvancados] = useState(false);
  const [tipo, setTipo] = useState(null);
  const [cnpj, setCnpj] = useState("");
  const [dataInicio, setDataInicio] = useState(null);
  const [dataFim, setDataFim] = useState(null);
  const [status, setStatus] = useState(null);
  const [arquivo, setArquivo] = useState("");
  const [buscaRapida, setBuscaRapida] = useState("");

  // Modal de logs
  const [openModalLogs, setOpenModalLogs] = useState(false);
  const [importacaoSelecionada, setImportacaoSelecionada] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Ordenação
  const [sortBy, setSortBy] = useState("data_inicio");
  const [sortOrder, setSortOrder] = useState("desc");

  // Estatísticas do dia
  const [estatisticasDia, setEstatisticasDia] = useState({
    sucesso: 0,
    erro: 0,
    aviso: 0,
    loading: false
  });

  // Ref para controlar se já carregou na montagem inicial
  const isInitialMount = useRef(true);

  const [tiposImportacao, setTiposImportacao] = useState([
    { value: "SPED Fiscal", label: "SPED Fiscal" },
    { value: "SPED Contribuições", label: "SPED Contribuições" },
    { value: "SPED ECD", label: "SPED ECD" },
    { value: "SPED ECF", label: "SPED ECF" },
    { value: "XML NF-e", label: "XML NF-e" },
    { value: "XML NFS-e", label: "XML NFS-e" },
    { value: "XML CTe", label: "XML CTe" },
    { value: "XML CF-e", label: "XML CF-e" },
    { value: "eSocial", label: "eSocial" },
  ]);

  const statusOptions = [
    { value: "salvo com sucesso", label: "Sucesso" },
    { value: "falha na importacao", label: "Aviso" },
    { value: "erro", label: "Erro" },
  ];

  // Formata data em horário local como YYYY-MM-DD (evita toISOString que usa UTC e pode mudar o dia).
  const formatarDataLocal = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  // Função para verificar se há filtros manuais ativos (tipo, status, cnpj, arquivo, buscaRapida).
  // Qualquer filtro, incluindo período (dataInicio/dataFim), desativa a regra de data atual.
  const temFiltrosManuais = () => {
    return (
      (tipo && tipo.value) ||
      (status && status.value) ||
      (cnpj && cnpj.trim()) ||
      (arquivo && arquivo.trim()) ||
      (buscaRapida && buscaRapida.trim())
    );
  };

  // Função para buscar estatísticas com filtros aplicados
  const handleBuscarEstatisticas = async () => {
    try {
      setEstatisticasDia(prev => ({ ...prev, loading: true }));

      // Construir parâmetros baseados nos filtros ativos
      const params = {};

      // Verificar se há filtros manuais ativos
      const temFiltros = temFiltrosManuais();

      // REGRA DE NEGÓCIO CRÍTICA (MESMA DO handleBuscarDados):
      // 1. Se NÃO houver filtros manuais e NÃO houver data explícita → aplicar data atual automaticamente
      // 2. Qualquer filtro (incluindo período) desativa a regra de data atual; usar só o que foi selecionado

      // Adicionar filtros apenas se preenchidos
      if (tipo && tipo.value) params.tipo = tipo.value;
      if (cnpj) {
        const cnpjLimpo = cnpj.replace(/\D/g, "");
        if (cnpjLimpo.length > 0) params.cnpj = cnpjLimpo;
      }

      // Aplicar filtro de data EXPLÍCITA (se o usuário selecionou; usar data local)
      if (dataInicio) {
        const date = new Date(dataInicio);
        params.dataInicio = formatarDataLocal(date);
      }
      if (dataFim) {
        const date = new Date(dataFim);
        params.dataFim = formatarDataLocal(date);
      }

      // Datas: o backend aplica hoje quando nenhuma data é enviada.
      if (status && status.value) {
        params.status = status.value;
      }
      if (arquivo && arquivo.trim()) params.arquivo = arquivo.trim();
      if (buscaRapida && buscaRapida.trim()) params.buscaRapida = buscaRapida.trim();

      const response = await buscarEstatisticas(params);

      setEstatisticasDia({
        sucesso: response?.sucesso ?? 0,
        erro: response?.erro ?? 0,
        aviso: response?.aviso ?? 0,
        loading: false
      });
    } catch (error) {
      console.error("Erro ao buscar estatísticas:", error);
      setEstatisticasDia(prev => ({ ...prev, sucesso: 0, erro: 0, aviso: 0, loading: false }));
    }
  };

  // Função para buscar dados (similar ao handleBuscarDados da tela de Relatórios)
  const handleBuscarDados = async () => {
    try {
      setLoading(true);

      // Sempre incluir paginação e ordenação
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy,
        sortOrder,
      };

      // Verificar se há filtros manuais ativos
      const temFiltros = temFiltrosManuais();

      // REGRA DE NEGÓCIO CRÍTICA:
      // 1. Se NÃO houver filtros manuais e NÃO houver data explícita → aplicar data atual automaticamente
      // 2. Qualquer filtro (incluindo período) desativa a regra de data atual; usar só o que foi selecionado

      // Adicionar filtros apenas se preenchidos
      if (tipo && tipo.value) params.tipo = tipo.value;
      if (cnpj) {
        const cnpjLimpo = cnpj.replace(/\D/g, "");
        if (cnpjLimpo.length > 0) params.cnpj = cnpjLimpo;
      }

      // Aplicar filtro de data EXPLÍCITA (se o usuário selecionou; usar data local)
      if (dataInicio) {
        const date = new Date(dataInicio);
        params.dataInicio = formatarDataLocal(date);
      }
      if (dataFim) {
        const date = new Date(dataFim);
        params.dataFim = formatarDataLocal(date);
      }

      // Datas: o backend aplica hoje quando nenhuma data é enviada.
      // Só enviar as datas que o usuário preencheu explicitamente.
      if (status && status.value) {
        params.status = status.value;
      }
      if (arquivo && arquivo.trim()) params.arquivo = arquivo.trim();
      if (buscaRapida && buscaRapida.trim()) params.buscaRapida = buscaRapida.trim();

      const response = await buscarImportacoes(params);

      if (response == null || response === undefined) {
        setData([]);
        setTotal(0);
        return;
      }
      const list = response?.data;
      const totalCount = response?.pagination?.total;
      if (Array.isArray(list)) {
        setData(list);
        setTotal(totalCount ?? 0);
      } else if (Array.isArray(response)) {
        setData(response);
        setTotal(response.length);
      } else {
        setData([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Erro ao carregar importações:", error?.message || error);
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Buscar tipos disponíveis na base
  useEffect(() => {
    const carregarTipos = async () => {
      try {
        const response = await buscarTipos();
        const tiposDaBase = response?.tipos || [];
        if (tiposDaBase.length > 0) {
          const tiposFormatados = tiposDaBase.map(tipo => ({
            value: tipo,
            label: tipo
          }));
          setTiposImportacao(tiposFormatados);
        }
      } catch (error) {
        console.error("Erro ao buscar tipos da base:", error);
        // Mantém os tipos padrão se falhar
      }
    };
    carregarTipos();
  }, []);

  // Carregar dados na montagem inicial
  useEffect(() => {
    Promise.all([handleBuscarDados(), handleBuscarEstatisticas()])
      .catch((err) => {
        console.error("Erro ao carregar dados iniciais (Importadores):", err?.message || err);
      });
    isInitialMount.current = false;
  }, []);

  // Carregar dados apenas quando paginação ou ordenação mudarem (não quando filtros mudarem)
  useEffect(() => {
    // Não carregar na montagem inicial (já carregado no useEffect acima)
    if (isInitialMount.current) {
      return;
    }
    Promise.all([handleBuscarDados(), handleBuscarEstatisticas()])
      .catch((err) => {
        console.error("Erro ao recarregar importações:", err?.message || err);
      });
  }, [page, rowsPerPage, sortBy, sortOrder]);

  const handleBuscaRapidaChange = (event) => {
    setBuscaRapida(event.target.value);
    // Não dispara busca automaticamente - apenas atualiza o estado
  };

  const handleFiltrar = () => {
    setPage(0);
    setLoadingFiltro(true);
    Promise.all([handleBuscarDados(), handleBuscarEstatisticas()])
      .finally(() => setLoadingFiltro(false));
  };

  const handleLimparFiltros = () => {
    setTipo(null);
    setCnpj("");
    setDataInicio(null);
    setDataFim(null);
    setStatus(null);
    setArquivo("");
    setBuscaRapida("");
    setPage(0);
    // Buscar dados após limpar filtros
    Promise.all([handleBuscarDados(), handleBuscarEstatisticas()]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // O useEffect já vai recarregar os dados quando page mudar
  };

  const handleChangeRowsPerPage = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // O useEffect já vai recarregar os dados quando rowsPerPage mudar
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const formatarCNPJ = (cnpj) => {
    if (!cnpj) return "";
    const cnpjLimpo = cnpj.replace(/\D/g, "");
    return cnpjLimpo.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  };

  const formatarData = (data) => {
    if (!data) return "-";
    const date = new Date(data);
    // Usar métodos locais para garantir que a hora seja exibida no fuso horário local
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const getStatusBadge = (status) => {
    // Padrão da tela: apenas 3 opções - Sucesso, Aviso, Erro (conforme filtro Status)
    // Status "info" (ex.: arquivo salvo que veio como info) é exibido como SUCESSO
    let tipoLog = status;

    if (status === "salvo com sucesso") {
      tipoLog = "success";
    } else if (status === "erro") {
      tipoLog = "error";
    } else if (status === "falha na importacao") {
      tipoLog = "warning";
    } else if (status === "processando" || status === "pendente" || status === "info") {
      tipoLog = "success";
    }

    const logConfig = {
      success: {
        label: "SUCESSO",
        bgColor: "#e8f5e9",
        textColor: "#2e7d32",
        icon: "✅"
      },
      warning: {
        label: "AVISO",
        bgColor: "#fff8e1",
        textColor: "#f57c00",
        icon: "⚠️"
      },
      error: {
        label: "ERRO",
        bgColor: "#ffebee",
        textColor: "#c62828",
        icon: "❌"
      },
    };

    const config = logConfig[tipoLog] || logConfig.success;
    return (
      <Chip
        label={`${config.icon} ${config.label}`}
        sx={{
          backgroundColor: config.bgColor,
          color: config.textColor,
          fontWeight: 600,
          fontSize: "0.75rem",
          height: "28px",
          "& .MuiChip-label": {
            paddingLeft: "8px",
            paddingRight: "8px",
          },
        }}
      />
    );
  };

  const handleVisualizarLogs = async (importacao) => {
    setImportacaoSelecionada(importacao);
    setOpenModalLogs(true);
    setLoadingLogs(true);
    try {
      const response = await buscarLogsImportacao(importacao.id);
      setLogs(response?.logs ?? []);
    } catch (error) {
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const getLogBadge = (tipoLog) => {
    const logConfig = {
      info: { label: "INFO", bgColor: "#e3f2fd", textColor: "#1565c0", icon: "ℹ️" },
      warning: { label: "AVISO", bgColor: "#fff8e1", textColor: "#f57c00", icon: "⚠️" },
      error: { label: "ERRO", bgColor: "#ffebee", textColor: "#c62828", icon: "❌" },
      success: { label: "SUCESSO", bgColor: "#e8f5e9", textColor: "#2e7d32", icon: "✅" },
    };

    const config = logConfig[tipoLog] || logConfig.info;
    return (
      <Chip
        label={`${config.icon} ${config.label}`}
        size="small"
        sx={{
          backgroundColor: config.bgColor,
          color: config.textColor,
          fontWeight: 600,
          fontSize: "0.75rem",
        }}
      />
    );
  };

  const formatarQuantidade = (valor) => {
    if (valor === null || valor === undefined) return "-";
    return valor.toLocaleString("pt-BR");
  };

  const formatarTimestampLog = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    // Usar métodos locais para garantir que a hora seja exibida no fuso horário local
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <div className={styles.container}>
      <Loading loading={loadingFiltro} overlay={true} />







      {/* Header */}

      <div className={styles.header}>
        {/* Título, Cards e Filtros na mesma linha */}
        <div style={{ display: "flex", alignItems: "center", width: "100%", gap: "1rem", position: "relative" }}>
          {/* Título */}
          <div className={styles.title} style={{ flexShrink: 0 }}>
            <FormatListBulletedIcon sx={{ fontSize: "2rem", color: "#18316e" }} />
            <h1 className={styles.titleText}>Importadores</h1>
          </div>

          {/* Cards de status - centralizados no espaço disponível */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: 0.75, sm: 1, md: 1.25 },
              alignItems: "center",
              height: { xs: "auto", sm: "40px" },
              flex: 1,
              justifyContent: "center",
              position: { xs: "relative", sm: "absolute" },
              left: { xs: "auto", sm: "50%" },
              transform: { xs: "none", sm: "translateX(-50%)" },
              flexWrap: { xs: "wrap", md: "nowrap" },
              marginTop: { xs: 1, sm: 0 }
            }}
          >
            {/* Sucesso */}
            <Paper
              sx={{
                minHeight: { xs: "36px", sm: "40px" },
                height: "auto",
                px: { xs: 1.5, sm: 2, md: 2.5 },
                py: { xs: 0.5, sm: 0.75 },
                display: "flex",
                alignItems: "center",
                borderRadius: 1.5,
                backgroundColor: "#ffffff",
                borderLeft: "4px solid #2e7d32",
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                minWidth: { xs: "120px", sm: "140px", md: "160px" },
                maxWidth: { xs: "100%", sm: "200px", md: "220px" },
                flex: { xs: "1 1 auto", sm: "0 1 auto" },
                overflow: "hidden"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.75, sm: 1, md: 1.5 }, width: "100%" }}>
                <Box
                  sx={{
                    minWidth: { xs: 24, sm: 26, md: 28 },
                    minHeight: { xs: 24, sm: 26, md: 28 },
                    height: { xs: 24, sm: 26, md: 28 },
                    paddingX: { xs: 0.5, sm: 0.75 },
                    borderRadius: "50%",
                    backgroundColor: "#2e7d32",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                    fontWeight: 700,
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    overflow: "visible"
                  }}
                >
                  {estatisticasDia.loading ? "..." : formatarQuantidade(estatisticasDia.sucesso)}
                </Box>

                <Typography
                  sx={{
                    color: "#2e7d32",
                    fontWeight: 600,
                    fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  Sucesso
                </Typography>
              </Box>
            </Paper>

            {/* Erro */}
            <Paper
              sx={{
                minHeight: { xs: "36px", sm: "40px" },
                height: "auto",
                px: { xs: 1.5, sm: 2, md: 2.5 },
                py: { xs: 0.5, sm: 0.75 },
                display: "flex",
                alignItems: "center",
                borderRadius: 1.5,
                backgroundColor: "#ffffff",
                borderLeft: "4px solid #c62828",
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                minWidth: { xs: "120px", sm: "140px", md: "160px" },
                maxWidth: { xs: "100%", sm: "200px", md: "220px" },
                flex: { xs: "1 1 auto", sm: "0 1 auto" },
                overflow: "hidden"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.75, sm: 1, md: 1.5 }, width: "100%" }}>
                <Box
                  sx={{
                    minWidth: { xs: 24, sm: 26, md: 28 },
                    minHeight: { xs: 24, sm: 26, md: 28 },
                    height: { xs: 24, sm: 26, md: 28 },
                    paddingX: { xs: 0.5, sm: 0.75 },
                    borderRadius: "50%",
                    backgroundColor: "#c62828",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                    fontWeight: 700,
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    overflow: "visible"
                  }}
                >
                  {estatisticasDia.loading ? "..." : formatarQuantidade(estatisticasDia.erro)}
                </Box>

                <Typography
                  sx={{
                    color: "#c62828",
                    fontWeight: 600,
                    fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  Erro
                </Typography>
              </Box>
            </Paper>

            {/* Aviso */}
            <Paper
              sx={{
                minHeight: { xs: "36px", sm: "40px" },
                height: "auto",
                px: { xs: 1.5, sm: 2, md: 2.5 },
                py: { xs: 0.5, sm: 0.75 },
                display: "flex",
                alignItems: "center",
                borderRadius: 1.5,
                backgroundColor: "#ffffff",
                borderLeft: "4px solid #f57c00",
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                minWidth: { xs: "120px", sm: "140px", md: "160px" },
                maxWidth: { xs: "100%", sm: "200px", md: "220px" },
                flex: { xs: "1 1 auto", sm: "0 1 auto" },
                overflow: "hidden"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.75, sm: 1, md: 1.5 }, width: "100%" }}>
                <Box
                  sx={{
                    minWidth: { xs: 24, sm: 26, md: 28 },
                    minHeight: { xs: 24, sm: 26, md: 28 },
                    height: { xs: 24, sm: 26, md: 28 },
                    paddingX: { xs: 0.5, sm: 0.75 },
                    borderRadius: "50%",
                    backgroundColor: "#f57c00",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
                    fontWeight: 700,
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    overflow: "visible"
                  }}
                >
                  {estatisticasDia.loading ? "..." : formatarQuantidade(estatisticasDia.aviso)}
                </Box>

                <Typography
                  sx={{
                    color: "#f57c00",
                    fontWeight: 600,
                    fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  Aviso
                </Typography>
              </Box>
            </Paper>
          </Box>

          {/* Badge de período (alinhado com os cards) */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "40px",
              flexShrink: 0,
              ml: "auto",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: "#475569",
                fontSize: "0.75rem",
                fontWeight: 500,
                mb: 0.25,
                lineHeight: 1,
              }}
            >
              Filtros Aplicados
            </Typography>
            <Chip
              icon={<AssignmentIcon sx={{ fontSize: "0.875rem !important" }} />}
              label={(() => {
                // Se não houver filtros, mostrar que está exibindo todos os arquivos
                if (!tipo && !cnpj && !dataInicio && !dataFim && !status && !arquivo && !buscaRapida) {
                  return "Todas as importações";
                }

                // Se houver filtros, construir frase descritiva (simplificada)
                const filtros = [];
                if (tipo && tipo.value) filtros.push(tipo.label);
                if (cnpj) {
                  const cnpjLimpo = cnpj.replace(/\D/g, "");
                  if (cnpjLimpo.length > 0) {
                    filtros.push(`CNPJ: ${formatarCNPJ(cnpjLimpo)}`);
                  }
                }
                if (arquivo && arquivo.trim()) {
                  filtros.push(`Arquivo: ${arquivo.trim().length > 20 ? arquivo.trim().substring(0, 20) + "..." : arquivo.trim()}`);
                }
                if (dataInicio && dataFim) {
                  const inicio = new Date(dataInicio).toLocaleDateString("pt-BR");
                  const fim = new Date(dataFim).toLocaleDateString("pt-BR");
                  if (inicio === fim) {
                    filtros.push(`Data Importação: ${inicio}`);
                  } else {
                    filtros.push(`Data Importação: ${inicio} a ${fim}`);
                  }
                } else if (dataInicio) {
                  const inicio = new Date(dataInicio).toLocaleDateString("pt-BR");
                  const hoje = new Date().toLocaleDateString("pt-BR");
                  filtros.push(`Data Importação: ${inicio} a ${hoje}`);
                } else if (dataFim) {
                  filtros.push(`Data Importação até ${new Date(dataFim).toLocaleDateString("pt-BR")}`);
                }
                if (status && status.value) filtros.push(status.label);

                // Limitar tamanho da frase
                let frase = filtros.length > 0
                  ? `Importações filtradas: ${filtros.join(", ")}`
                  : "Todas as importações";

                // Truncar se muito longo
                if (frase.length > 60) {
                  frase = frase.substring(0, 57) + "...";
                }

                return frase;
              })()}
              sx={{
                height: "26px",
                fontSize: "0.75rem",
                fontWeight: 500,
                backgroundColor: "#ffffff",
                color: "#475569",
                border: "1px solid #90caf9",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                cursor: "pointer",
                borderRadius: 1.5,
                "& .MuiChip-label": {
                  paddingLeft: "6px",
                  paddingRight: "8px",
                },
              }}
            />
          </Box>
        </div>
      </div>


      {/* Filtros Avançados */}
      <Paper className={styles.filtrosCard}>
        <div className={styles.filtrosHeader}>
          <FilterListIcon className={styles.iconFilter} />
          <Typography variant="h6">Filtros Avançados</Typography>
        </div>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 1, sm: 2 },
            mt: 2,
            alignItems: "center",
            maxWidth: "100%",

            "& > *": {
              flex: {
                xs: "1 1 100%",
                sm: "1 1 calc(50% - 8px)",
                md: "1 1 180px",
                lg: "1 1 150px",
              },
              minWidth: {
                xs: "100%",
                sm: "calc(50% - 8px)",
                md: "180px",
                lg: "150px",
              },
              maxWidth: {
                xs: "100%",
                sm: "calc(50% - 8px)",
                md: "220px",
                lg: "none",
              },
            },

            /* 🔒 Impede quebra dos labels */
            "& .MuiInputLabel-root": {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },

            /* 🔒 Mantém altura consistente sem quebrar layout */
            "& .MuiInputBase-root": {
              height: "40px",
            },

            "& .MuiOutlinedInput-input": {
              padding: "10px 14px",
            },
          }}
        >
          <Box>
            <Autocomplete
              size="small"
              options={tiposImportacao}
              getOptionLabel={(option) => option?.label || option || ""}
              value={tipo}
              onChange={(e, newValue) => setTipo(newValue)}
              isOptionEqualToValue={(option, value) =>
                option?.value === value?.value
              }
              renderInput={(params) => (
                <TextField {...params} label="Tipo" placeholder="Todos os tipos" />
              )}
            />
          </Box>

          <Box>
            <TextField
              size="small"
              label="CNPJ"
              value={cnpj ? formatarCNPJ(cnpj) : ""}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                if (value.length <= 14) setCnpj(value);
              }}
              placeholder="00.000.000/0000-00"
              inputProps={{ maxLength: 18 }}
              InputProps={{
                endAdornment: cnpj && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setCnpj("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
          </Box>

          <Box>
            <GenericDatePicker
              label="De"
              value={dataInicio}
              onChange={(date) => setDataInicio(date)}
              placeholder="dd/mm/aaaa"
              clearable
              onClear={() => setDataInicio(null)}
            />
          </Box>

          <Box>
            <GenericDatePicker
              label="Até"
              value={dataFim}
              onChange={(date) => setDataFim(date)}
              placeholder="dd/mm/aaaa"
              clearable
              onClear={() => setDataFim(null)}
            />
          </Box>

          <Box>
            <Autocomplete
              size="small"
              options={statusOptions}
              getOptionLabel={(option) => option?.label || option || ""}
              value={status}
              onChange={(e, newValue) => setStatus(newValue)}
              isOptionEqualToValue={(option, value) =>
                option?.value === value?.value
              }
              renderInput={(params) => (
                <TextField {...params} label="Status" placeholder="Todos" />
              )}
            />
          </Box>

          <Box>
            <TextField
              size="small"
              label="Arquivo"
              value={arquivo}
              onChange={(e) => setArquivo(e.target.value)}
              placeholder="Nome do arquivo..."
              InputProps={{
                endAdornment: arquivo && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setArquivo("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
          </Box>

          {/* Botões */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: { xs: "flex-start", md: "flex-end" },
              minWidth: "fit-content",
              "& button": {
                height: "40px",
                minHeight: "40px",
                padding: "0 14px",
                boxSizing: "border-box",
                display: "inline-flex",
                alignItems: "center",
              },
            }}
          >
            <BlueButton
              clickFunction={handleFiltrar}
              contentStartIcon={<SearchIcon />}
              textValue="Filtrar"
            />
            <WhiteButton
              clickFunction={handleLimparFiltros}
              contentStartIcon={<CloseIcon />}
              textValue="Limpar"
            />
          </Box>
        </Box>
      </Paper>


      {/* Tabela */}
      <Paper className={styles.tableCard}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f7fa" }}>
                <TableCell sx={{
                  minWidth: "100px",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  padding: "0.75rem 1rem",
                  backgroundColor: "#f5f7fa",
                  borderBottom: "2px solid #e0e0e0"
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }} onClick={() => handleSort("tipo")}>
                    <strong>Tipo</strong>
                    <ArrowDownwardIcon sx={{ fontSize: "0.875rem", opacity: sortBy === "tipo" ? 1 : 0.3 }} />
                  </Box>
                </TableCell>
                <TableCell sx={{
                  minWidth: "120px",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  padding: "0.75rem 1rem",
                  textAlign: "center",
                  backgroundColor: "#f5f7fa",
                  borderBottom: "2px solid #e0e0e0"
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", justifyContent: "center" }} onClick={() => handleSort("cnpj")}>
                    <strong>CNPJ</strong>
                    <ArrowDownwardIcon sx={{ fontSize: "0.875rem", opacity: sortBy === "cnpj" ? 1 : 0.3 }} />
                  </Box>
                </TableCell>
                <TableCell sx={{
                  minWidth: "280px",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  padding: "0.75rem 1rem",
                  backgroundColor: "#f5f7fa",
                  borderBottom: "2px solid #e0e0e0"
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }} onClick={() => handleSort("arquivo")}>
                    <strong>Arquivo</strong>
                    <ArrowDownwardIcon sx={{ fontSize: "0.875rem", opacity: sortBy === "arquivo" ? 1 : 0.3 }} />
                  </Box>
                </TableCell>
                <TableCell sx={{
                  minWidth: "140px",
                  whiteSpace: "nowrap",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  padding: "0.75rem 1rem",
                  backgroundColor: "#f5f7fa",
                  borderBottom: "2px solid #e0e0e0"
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }} onClick={() => handleSort("data_inicio")}>
                    <strong>Data Importação</strong>
                    <ArrowDownwardIcon sx={{ fontSize: "0.875rem", opacity: sortBy === "data_inicio" ? 1 : 0.3 }} />
                  </Box>
                </TableCell>
                <TableCell sx={{
                  minWidth: "120px",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  padding: "0.75rem 1rem",
                  textAlign: "center",
                  backgroundColor: "#f5f7fa",
                  borderBottom: "2px solid #e0e0e0"
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", justifyContent: "center" }} onClick={() => handleSort("status")}>
                    <strong>Status</strong>
                    <ArrowDownwardIcon sx={{ fontSize: "0.875rem", opacity: sortBy === "status" ? 1 : 0.3 }} />
                  </Box>
                </TableCell>
                <TableCell sx={{
                  minWidth: "150px",
                  width: "1%",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  padding: "0.75rem 1rem",
                  backgroundColor: "#f5f7fa",
                  borderBottom: "2px solid #e0e0e0"
                }}>
                  <strong>Ações</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Loading />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma importação encontrada
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{
                      padding: "0.5rem 1rem",
                      fontSize: "0.875rem"
                    }}>
                      {row.tipo || row.tipo_importacao || "-"}
                    </TableCell>
                    <TableCell sx={{
                      whiteSpace: "nowrap",
                      padding: "0.5rem 1rem",
                      fontSize: "0.875rem",
                      textAlign: "center"
                    }}>
                      {row.cnpj ? formatarCNPJ(row.cnpj) : "-"}
                    </TableCell>
                    <TableCell sx={{
                      maxWidth: 520,
                      minWidth: 280,
                      padding: "0.5rem 1rem",
                      fontSize: "0.875rem"
                    }}>
                      {row.arquivo ? (
                        <Tooltip title={row.arquivo} arrow>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handleVisualizarLogs(row);
                            }}
                            style={{
                              color: "#1e4a7a",
                              textDecoration: "underline",
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontWeight: "normal"
                            }}
                          >
                            {row.arquivo}
                          </a>
                        </Tooltip>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell sx={{
                      whiteSpace: "nowrap",
                      padding: "0.5rem 1rem",
                      fontSize: "0.875rem"
                    }}>
                      {formatarData(row.data_inicio)}
                    </TableCell>
                    <TableCell sx={{
                      padding: "0.5rem 1rem",
                      fontSize: "0.875rem",
                      textAlign: "center"
                    }}>
                      {row.detalhes ? (
                        <Tooltip title={row.detalhes} arrow>
                          <span>{getStatusBadge(row.status)}</span>
                        </Tooltip>
                      ) : (
                        getStatusBadge(row.status)
                      )}
                    </TableCell>
                    <TableCell sx={{
                      width: "1%",
                      padding: "0.5rem 1rem",
                      fontSize: "0.875rem",
                      whiteSpace: "nowrap"
                    }}>
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <BlueButton
                          clickFunction={() => handleVisualizarLogs(row)}
                          contentStartIcon={<DescriptionIcon />}
                          textValue="Visualizar logs"
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "1rem",
          flexWrap: "wrap",
          gap: "16px"
        }}>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => {
              const totalText = count !== -1 ? Number(count).toLocaleString("pt-BR") : `mais de ${to}`;
              return `${from}-${to} de ${totalText}`;
            }}
            sx={{
              flex: 1,
              "& .MuiTablePagination-toolbar": {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                paddingLeft: 0,
                paddingRight: 0
              },
              "& .MuiTablePagination-selectLabel": {
                margin: 0,
                fontSize: "0.875rem"
              },
              "& .MuiTablePagination-displayedRows": {
                margin: 0,
                fontSize: "0.875rem",
                fontWeight: 500
              },
              "& .MuiTablePagination-select": {
                borderRadius: "8px",
                fontSize: "0.875rem"
              }
            }}
          />
          <Box sx={{
            fontSize: "0.875rem",
            fontWeight: 600,
            color: "#18316e",
            whiteSpace: "nowrap",
            marginLeft: "16px"
          }}>
            Total: <strong style={{ fontSize: "1rem" }}>{(total || 0).toLocaleString("pt-BR")}</strong> {(total || 0) === 1 ? "registro" : "registros"}
          </Box>
        </Box>
      </Paper>

      {/* Modal de Logs */}
      <Dialog
        open={openModalLogs}
        onClose={() => setOpenModalLogs(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #0d2847 0%, #1e4a7a 100%)",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">📋 Logs de Processamento</Typography>
          <IconButton onClick={() => setOpenModalLogs(false)} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3, overflowX: "hidden" }}>
          {importacaoSelecionada && (
            <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f7fa", borderRadius: 2, overflow: "hidden" }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Arquivo
                  </Typography>
                  <Tooltip title={importacaoSelecionada.arquivo || "-"} arrow placement="top">
                    <Typography
                      variant="body1"
                      sx={{
                        wordBreak: "break-word",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        maxWidth: "100%",
                        lineHeight: 1.4
                      }}
                    >
                      {importacaoSelecionada.arquivo || "-"}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Tipo
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%"
                    }}
                  >
                    {importacaoSelecionada.tipo || importacaoSelecionada.tipo_importacao || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                    CNPJ
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: "monospace",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%"
                    }}
                  >
                    {formatarCNPJ(importacaoSelecionada.cnpj) || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Status
                  </Typography>
                  <Box sx={{ display: "inline-block" }}>
                    {getStatusBadge(importacaoSelecionada.status)}
                  </Box>
                </Grid>
                {/* So mostra o resumo aqui se ele trouxer informacao ALEM do ultimo log da lista
                    abaixo -- na maioria dos casos o "detalhes" da importacao e identico ao do
                    log mais recente (mesma origem no backend), e repetir os dois so poluia a
                    tela sem agregar nada. */}
                {importacaoSelecionada.detalhes &&
                  importacaoSelecionada.detalhes !== logs[logs.length - 1]?.detalhes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                      Detalhes
                    </Typography>
                    <Tooltip title={importacaoSelecionada.detalhes} arrow placement="top">
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          p: 1,
                          bgcolor: "white",
                          borderRadius: 1,
                          wordBreak: "break-word",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          maxWidth: "100%",
                          lineHeight: 1.5
                        }}
                      >
                        {importacaoSelecionada.detalhes}
                      </Typography>
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {loadingLogs ? (
            <Loading />
          ) : (
            <Box sx={{ maxHeight: "50vh", overflowY: "auto" }}>
              {logs.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                  Nenhum log encontrado
                </Typography>
              ) : (
                logs.map((log, index) => (
                  <Paper
                    key={log.id || index}
                    sx={{
                      p: 2,
                      mb: 2,
                      borderRadius: 2,
                      bgcolor: "white",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {formatarTimestampLog(log.timestamp)}
                      </Typography>
                      <Box sx={{ flexShrink: 0 }}>
                        {getLogBadge(log.tipo_log)}
                      </Box>
                    </Box>
                    <Tooltip title={log.mensagem} arrow placement="top">
                      <Typography
                        variant="body2"
                        sx={{
                          mb: log.detalhes ? 1 : 0,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          lineHeight: 1.5,
                          // Aplicar cor vermelha quando for erro
                          color: log.tipo_log === "error" ? "#c62828" : "inherit"
                        }}
                      >
                        {log.mensagem}
                      </Typography>
                    </Tooltip>
                    {log.detalhes && (
                      <Tooltip title={log.detalhes} arrow placement="top">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "block",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            lineHeight: 1.4
                          }}
                        >
                          <strong>Detalhes:</strong> {log.detalhes}
                        </Typography>
                      </Tooltip>
                    )}
                  </Paper>
                ))
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <WhiteButton clickFunction={() => setOpenModalLogs(false)} textValue="Fechar" />
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Importadores;
